/**
 * WasserijPage — Laundry dashboard: pipeline, incoming volume, production
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui';
import { PipelineView } from '../components/wasserij/PipelineView';
import { InkomendView } from '../components/wasserij/InkomendView';
import { ProductieView } from '../components/wasserij/ProductieView';
import { StatusDetail } from '../components/wasserij/StatusDetail';
import { StilstandView } from '../components/wasserij/StilstandView';
import { useWasserijStore } from '../stores';
import {
  parseItemParam,
  useWasserijItemDrawerStore,
} from '../stores/wasserij-item-drawer-store';

export function WasserijPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openItemId = useWasserijItemDrawerStore((s) => s.openItemId);
  const openItem = useWasserijItemDrawerStore((s) => s.open);
  const closeItem = useWasserijItemDrawerStore((s) => s.close);

  // URL ↔ drawer-store sync (zelfde patroon als OrderDetailPage) zodat een
  // vastgelopen kleed deelbaar is via /wasserij?item=<id> en refresh overleeft.
  useEffect(() => {
    const fromUrl = parseItemParam(searchParams.get('item'));
    if (fromUrl && fromUrl !== openItemId) openItem(fromUrl);
    if (!fromUrl && openItemId) closeItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const current = searchParams.get('item');
    if (openItemId && current !== openItemId) {
      setSearchParams(
        (sp) => {
          sp.set('item', openItemId);
          return sp;
        },
        { replace: true }
      );
    } else if (!openItemId && current) {
      setSearchParams(
        (sp) => {
          sp.delete('item');
          return sp;
        },
        { replace: true }
      );
    }
  }, [openItemId, searchParams, setSearchParams]);

  // Sluit drawer op page-unmount zodat hij niet blijft hangen bij route-wissel
  useEffect(() => {
    return () => closeItem();
  }, [closeItem]);

  const {
    pipeline,
    inkomend,
    productie,
    statusDetail,
    selectedStatus,
    stilstand,
    stilstandStale,
    stilstandSyncedAt,
    pipelineTotal,
    inkomendTotal7d,
    productieAvg14d,
    isLoadingPipeline,
    isLoadingInkomend,
    isLoadingProductie,
    isLoadingDetail,
    isLoadingStilstand,
    setSelectedStatus,
    startPolling,
    stopPolling,
  } = useWasserijStore();

  useEffect(() => {
    startPolling(300000); // 5 minutes
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto overflow-y-auto h-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-heading font-semibold text-main">Wasserij</h1>
          <p className="text-muted text-sm">
            Pipeline, inkomend volume en productie
          </p>
        </div>

        {/* Section 0: Vastgelopen kleden (alert) */}
        <Card variant="elevated" className="mb-6">
          <CardHeader>
            <CardTitle>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Vastgelopen kleden
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StilstandView
              items={stilstand}
              isLoading={isLoadingStilstand}
              stale={stilstandStale}
              syncedAt={stilstandSyncedAt}
            />
          </CardContent>
        </Card>

        {/* Section 1: Inkomend */}
        <Card variant="elevated" className="mb-6">
          <CardHeader>
            <CardTitle>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                Inkomend — ophaalplanning
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InkomendView
              dagen={inkomend}
              total7d={inkomendTotal7d}
              isLoading={isLoadingInkomend}
            />
          </CardContent>
        </Card>

        {/* Section 2: Pipeline */}
        <Card variant="elevated" className="mb-6">
          <CardHeader>
            <CardTitle>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Wasserij Pipeline
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PipelineView
              pipeline={pipeline}
              total={pipelineTotal}
              isLoading={isLoadingPipeline}
              onStatusClick={setSelectedStatus}
            />
          </CardContent>
        </Card>

        {/* Section 3: Productie */}
        <Card variant="elevated">
          <CardHeader>
            <CardTitle>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Productie — ingepakt (14 dagen)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProductieView
              productie={productie}
              avg14d={productieAvg14d}
              isLoading={isLoadingProductie}
            />
          </CardContent>
        </Card>
      </div>

      {/* Status detail slide-over */}
      {selectedStatus && (
        <StatusDetail
          status={selectedStatus}
          items={statusDetail}
          isLoading={isLoadingDetail}
          onClose={() => setSelectedStatus(null)}
        />
      )}
    </>
  );
}
